import React, { type FC, useEffect, useState } from 'react';

import BulkNavigationActions from '@repo/ui/components/custom/navigation-bar/bulk-actions/component';
import {
  NavigationBarProps,
  Tab,
} from '@repo/ui/components/custom/navigation-bar/types';
import { useApplyViewPermissions, useScreenSize } from '@repo/ui/hooks';
import { cn } from '@repo/ui/lib/utils';
import { useViewPermissions, ViewMode } from '@repo/ui/state';
import { Button } from '../../ui/button';
import { Skeleton } from '../../ui/skeleton';
import { Header } from './components/header';
import { ToolbarSection } from './components/toolbar-section';

export const NavigationBar: FC<NavigationBarProps> = ({
  isDismissedStage,
  backButtonTitle,
  tabs,
  activeTab,
  uiConfig,
  loading,
  error,
  viewMode,
  isJuryStage,
  isFinalistsStage,
  onSearchClick,
  onSelectClick,
  onNewStageClick,
  onBackButtonClick,
  onDismissedFolderClick,
  dismissedCount,
  canCreateStage,
  openCallId,
}) => {
  const isErrorOnLoading = Boolean(!loading && error && !tabs?.length);
  const { isSmall } = useScreenSize();
  const { data: permissions } = useViewPermissions();
  useApplyViewPermissions(permissions);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [showInputForEdit, setShowInputForEdit] = useState({
    tabId: '',
    show: false,
  });
  const [tabInputValue, setTabInputValue] = useState('');
  const internalTabs = tabs.map((tab) => ({
    ...tab,
    count: new Intl.NumberFormat('en-GB', {
      maximumSignificantDigits: 3,
    }).format(tab.value),
    active: activeTab === tab.id,
  }));

  const onRenameDone = (tab: Tab) => {
    setShowInputForEdit({ tabId: '', show: false });
    tab.onEditTabName(tab);
  };

  const rowBackEdit = (tab: Tab) => {
    setShowInputForEdit({ tabId: '', show: false });
    setTabInputValue(tab.name);
  };

  const pressKeyHandler = (
    e: React.KeyboardEvent<HTMLInputElement>,
    tab: Tab,
  ) => {
    if (e.key === 'Enter') {
      onRenameDone({
        ...tab,
        name: tabInputValue,
      });
    }
    if (e.key === 'Escape') {
      rowBackEdit(tab);
    }
  };

  useEffect(() => {
    if (showInputForEdit.show) {
      const tab = internalTabs.find((t) => t.id === showInputForEdit.tabId);
      if (tab) {
        setTabInputValue(tab.name);
      }
    }
  }, [showInputForEdit]);

  return (
    <nav className="flex flex-col w-full gap-4">
      <Header
        dismissedCount={dismissedCount}
        error={error}
        loading={loading}
        viewMode={viewMode}
        backButtonTitle={backButtonTitle}
        isDismissedStage={isDismissedStage}
        onNewStageClick={onNewStageClick}
        onBackButtonClick={onBackButtonClick}
        onDismissedFolderClick={onDismissedFolderClick}
        canCreateStage={canCreateStage}
      />
      <main className="flex flex-col justify-between items-start gap-2 md:h-16 md:items-center md:flex-row md:border-b md:px-4 max-w-full">
        {viewMode === ViewMode.VIEW && !isDismissedStage && (
          <div
            className={cn(
              'flex w-full items-center h-12 border-b md:border-none max-w-full',
              {
                'overflow-x-auto overflow-y-hidden': isSmall,
              },
            )}
          >
            {loading &&
              new Array(3).fill(0).map((_, index) => (
                <Button
                  key={index}
                  variant="ghost"
                  data-testid={`tab-skeleton-${index}`}
                  className="flex items-center gap-1.5 pl-0 hover:bg-transparent group"
                >
                  <Skeleton className="h-3 w-14" />
                  <Skeleton className="h-3 w-8" />
                </Button>
              ))}
            {isErrorOnLoading && <span className="text-red-600">{error}</span>}
            {!loading &&
              internalTabs.map((tab) => (
                <React.Fragment key={tab.id}>
                  {showInputForEdit.tabId === tab.id && (
                    <input
                      type="text"
                      value={tabInputValue}
                      onChange={(e) => {
                        setTabInputValue(e.target.value);
                      }}
                      autoFocus
                      onKeyDown={(e) => pressKeyHandler(e, tab)}
                      onKeyUp={(e) => pressKeyHandler(e, tab)}
                      onBlur={() => rowBackEdit(tab)}
                      className="p-1 mr-4 bg-muted max-w-fit active:outline-none focus:outline-none focus:ring-0 focus:ring-transparent"
                    />
                  )}
                  <Button
                    key={tab.name}
                    variant="ghost"
                    className={cn(
                      'flex items-center gap-1.5 pl-0 hover:bg-transparent group',
                      {
                        hidden: showInputForEdit.tabId === tab.id,
                      },
                    )}
                    onClick={() => tab.onTabClick(tab)}
                    onDoubleClick={() => {
                      if (!tab.canBeEdited) return;
                      setShowInputForEdit({
                        tabId: tab.id,
                        show: true,
                      });
                    }}
                  >
                    <span
                      className={cn(
                        'group-hover:underline underline-offset-4',
                        {
                          'font-semibold underline': tab.active,
                        },
                      )}
                    >
                      {tab.name}
                    </span>
                    <span className="text-muted-foreground text-sm font-medium leading-5">
                      {tab.count}
                    </span>
                  </Button>
                </React.Fragment>
              ))}
          </div>
        )}
        {isDismissedStage && (
          <div className="hidden md:flex items-center text-zinc-500 text-sm w-full pl-1 h-12">
            Dismissed Folder {dismissedCount}
          </div>
        )}
        {viewMode === ViewMode.VIEW && (
          <ToolbarSection
            openCallId={openCallId}
            error={error}
            filterDefinitions={uiConfig?.filterDefinitions}
            filtersEnabled={uiConfig?.filtersEnabled}
            loading={loading}
            isJuryStage={isJuryStage}
            isFilterOpen={isFilterOpen}
            isFinalistsStage={isFinalistsStage}
            onSearchClick={onSearchClick}
            onSelectClick={onSelectClick}
            setIsFilterOpen={setIsFilterOpen}
          />
        )}
        {viewMode === ViewMode.SELECT && (
          <BulkNavigationActions
            tabs={tabs}
            activeTab={activeTab}
            bulkActionsConfig={uiConfig?.bulkActions}
            error={error}
            loading={loading}
            viewMode={viewMode}
            isJuryStage={isJuryStage}
            onSelectClick={onSelectClick}
          />
        )}
      </main>
    </nav>
  );
};
